/**
 * SociBaba Custom Plan Calculator — live slider-based pricing (Pillar 2).
 *
 * User picks their own numbers (credits, SKUs, channels, seats, sends,
 * regions), quote recalculates in real time via POST /api/plans/quote.
 * Every knob is admin-configurable via the /api/admin/plans/matrix endpoint.
 */
import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Calculator, ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL as string;

interface Quote {
  monthly_inr: number;
  yearly_inr: number;
  quote_inr: number;
  quote_period: string;
  line_items: Array<{ label: string; amount: number }>;
  requires_enterprise: boolean;
}

const SLIDERS = [
  { key: "credits", label: "AI credits per month", min: 0, max: 200000, step: 500, unit: "credits" },
  { key: "skus",    label: "Product SKUs",           min: 0, max: 25000,  step: 50,  unit: "SKUs" },
  { key: "channels",label: "Social channels",        min: 0, max: 100,    step: 1,   unit: "channels" },
  { key: "seats",   label: "Team seats",             min: 1, max: 50,     step: 1,   unit: "seats" },
  { key: "extra_regions", label: "Extra countries",  min: 0, max: 10,     step: 1,   unit: "regions" },
  { key: "sms_sends", label: "SMS sends per month",  min: 0, max: 100000, step: 500, unit: "SMS" },
  { key: "wa_sends",  label: "WhatsApp sends per month", min: 0, max: 100000, step: 500, unit: "WA" },
] as const;

const DEFAULT_VALUES: Record<string, number> = {
  credits: 2000,
  skus: 100,
  channels: 5,
  seats: 3,
  extra_regions: 0,
  sms_sends: 1000,
  wa_sends: 500,
};

// Seed initial quote so first-visit doesn't render "—" while the debounce
// waits for the POST to /api/plans/quote. Base platform fee = ₹999.
const SEED_QUOTE: Quote = {
  monthly_inr: 999,
  yearly_inr: Math.round(999 * 12 * (1 - 0.166)),
  quote_inr: Math.round(999 * 12 * (1 - 0.166)),
  quote_period: "yearly",
  line_items: [{ label: "Platform base", amount: 999 }],
  requires_enterprise: false,
};

export function PlanCalculator() {
  const [values, setValues] = useState<Record<string, number>>(DEFAULT_VALUES);
  const [yearly, setYearly] = useState(true);
  const [quote, setQuote] = useState<Quote | null>(SEED_QUOTE);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      axios
        .post<Quote>(`${API}/api/plans/quote`, { ...values, yearly })
        .then(r => setQuote(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [values, yearly]);

  const priceStr = useMemo(() => {
    if (!quote) return "—";
    return `₹${quote.quote_inr.toLocaleString("en-IN")}`;
  }, [quote]);

  return (
    <div
      data-testid="plan-calculator"
      className="rounded-3xl border border-neutral-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <div className="flex items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-fuchsia-600 grid place-items-center text-white">
          <Calculator className="h-4 w-4" />
        </div>
        <h2 className="text-xl font-semibold tracking-tight">Build your own plan</h2>
      </div>
      <p className="text-sm text-neutral-600 max-w-xl">
        Pick every number that matters. Price recalculates instantly. Beyond
        the caps, we&apos;ll match you with an Enterprise quote.
      </p>

      {/* Billing toggle */}
      <div className="mt-6 mb-6 inline-flex items-center gap-1 rounded-full bg-neutral-100 p-1">
        <button
          onClick={() => setYearly(false)}
          data-testid="plan-calc-monthly"
          className={`px-4 py-1.5 rounded-full text-xs font-medium ${!yearly ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500"}`}
        >Monthly</button>
        <button
          onClick={() => setYearly(true)}
          data-testid="plan-calc-yearly"
          className={`px-4 py-1.5 rounded-full text-xs font-medium ${yearly ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500"}`}
        >Yearly · save 2 mo</button>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Sliders */}
        <div className="space-y-4">
          {SLIDERS.map(s => (
            <div key={s.key}>
              <div className="flex items-baseline justify-between text-sm mb-1">
                <label htmlFor={`slider-${s.key}`} className="font-medium text-neutral-800">{s.label}</label>
                <span className="text-neutral-500 tabular-nums">
                  {values[s.key].toLocaleString("en-IN")} <span className="text-[10px] uppercase tracking-widest">{s.unit}</span>
                </span>
              </div>
              <input
                id={`slider-${s.key}`}
                data-testid={`plan-calc-slider-${s.key}`}
                type="range"
                min={s.min}
                max={s.max}
                step={s.step}
                value={values[s.key]}
                onChange={e => setValues(v => ({ ...v, [s.key]: Number(e.target.value) }))}
                className="w-full accent-orange-500"
              />
            </div>
          ))}
        </div>

        {/* Quote panel */}
        <div className="md:sticky md:top-6 self-start">
          <motion.div
            key={priceStr + (yearly ? "y" : "m")}
            initial={{ scale: 0.98, opacity: 0.7 }}
            animate={{ scale: 1, opacity: 1 }}
            className="rounded-2xl bg-neutral-900 text-white p-6"
          >
            <div className="text-[10px] uppercase tracking-widest text-neutral-400">
              Your custom plan · {yearly ? "yearly" : "monthly"}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <div className="text-4xl font-semibold tabular-nums" data-testid="plan-calc-price">{priceStr}</div>
              <div className="text-xs text-neutral-400">{yearly ? "/ year" : "/ month"}</div>
            </div>
            {yearly && quote && (
              <div className="text-xs text-emerald-400 mt-0.5">
                = ₹{Math.round(quote.yearly_inr / 12).toLocaleString("en-IN")} / month effective
              </div>
            )}

            <div className="mt-5 space-y-1.5">
              {quote?.line_items.map(li => (
                <div key={li.label} className="text-xs flex items-center justify-between">
                  <span className="text-neutral-400">{li.label}</span>
                  <span className="tabular-nums">₹{li.amount.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>

            {quote?.requires_enterprise ? (
              <div className="mt-5 rounded-xl bg-fuchsia-500/10 border border-fuchsia-400/40 p-3">
                <div className="text-xs font-medium text-fuchsia-200 mb-1 inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Enterprise quote required
                </div>
                <div className="text-[11px] text-fuchsia-100/80">
                  Your usage exceeds our Agency caps. Let&apos;s design a custom deal — dedicated infra, SLA, white-label.
                </div>
                <a
                  href="/enterprise-contact"
                  data-testid="plan-calc-enterprise-cta"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-white"
                >Talk to sales <ArrowRight className="h-3 w-3" /></a>
              </div>
            ) : (
              <a
                href="/signup"
                data-testid="plan-calc-cta"
                className="mt-5 w-full h-10 rounded-xl bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5"
              >Start with this plan <ArrowRight className="h-3.5 w-3.5" /></a>
            )}

            {loading && (
              <div className="text-[10px] text-neutral-500 mt-3">Recalculating…</div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default PlanCalculator;
